import { Box } from "@mui/material";

import { Control, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  useListSubjectsSubjectsGetQuery,
  useListZonesZonesGetQuery,
} from "api";
import { can } from "authorization/authorization.util";
import { DevicePermissionEnum } from "authorization/types";
import { ButtonLink } from "components/ButtonLink/ButtonLink";
import { SelectInput } from "components/forms";
import { SectionCard } from "components/SectionCard/SectionCard";
import { useAuthenticatedUser } from "hooks/useAuthenticatedUser";

import { DeviceFormSchema } from "../forms/deviceFormSchema";

// Get the currently available subjects for the selected zone
const useSubjectList = (
  deviceId: string,
  control: Control<DeviceFormSchema>,
) => {
  const { t } = useTranslation();

  const {
    data: zoneData,
    isFetching: isLoadingZones,
    isError: isZoneError,
  } = useListZonesZonesGetQuery();

  const zoneId = useWatch({ control, name: "zone_id" });

  const zonePath = zoneData?.data?.find(({ id }) => id === zoneId)?.path;

  const {
    data: subjectData,
    isFetching: isLoadingSubjects,
    isError: isSubjectsError,
  } = useListSubjectsSubjectsGetQuery(
    {
      path: zonePath,
      embed: ["devices"],
    },
    { skip: !zonePath },
  );

  return {
    isLoading: isLoadingSubjects || isLoadingZones,
    isError: isZoneError || isSubjectsError,
    list: [
      {
        id: "",
        label: t("none_unassign_device"),
        value: "",
      },
      ...(subjectData?.data
        ?.filter(
          (item) =>
            !item.devices?.data?.length ||
            item.devices?.data?.every((d) => d.id === deviceId),
        )
        .map((item) => ({
          id: item.id,
          label: item.identifier,
          value: item.id,
        })) ?? []),
    ],
  };
};

type SubjectDropDownProps = {
  deviceId: string;
  control: Control<DeviceFormSchema>;
};

export function SubjectDropDown({ deviceId, control }: SubjectDropDownProps) {
  const { t } = useTranslation();
  const { permissions } = useAuthenticatedUser();

  const subjectId = useWatch({ control, name: "subject_id" });

  const { list: options, isLoading } = useSubjectList(deviceId, control);

  const disabled = !can(
    permissions,
    DevicePermissionEnum["device.assign_subject"],
  );

  return (
    <SectionCard title={t("subject")} gridArea={"subject"}>
      <Box width="100%">
        <SelectInput
          fieldName={"subject_id"}
          control={control}
          label={t("Subject ID")}
          options={options}
          disabled={disabled}
          isLoading={isLoading}
          placeholder={t("none_unassign_device")}
        />
      </Box>
      {subjectId ? (
        <ButtonLink to={`../subjects/${subjectId}`}>
          {t("view_selected_subject")}
        </ButtonLink>
      ) : (
        <Box height={"36.5px"} />
      )}
    </SectionCard>
  );
}
